/* talk·point — <tp-story-viewer>: Instagram-Style Story-Overlay.
   Öffnet bei Klick auf eine Bubble mit [data-story="1"] (Delegation).
   Kein externer Slider; reine rAF-Progress (pausierbar, kein Reflow im Hot-Path).
   IIFE + Guard gegen Doppel-Registrierung. */
(function () {
  if (customElements.get('tp-story-viewer')) return;
  // Story-Viewer v2 aktiv (Theme-Einstellung oder ?stories=v2, siehe tp-category-bubbles)
  // → v1 bleibt komplett still, damit nicht beide auf denselben Bubble-Klick reagieren.
  if (document.documentElement.dataset.tpStories === 'v2') return;

  const SEEN_KEY = 'tp:seen-stories';
  const readSeen = () => { try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')); } catch (e) { return new Set(); } };
  const writeSeen = (set) => { try { localStorage.setItem(SEEN_KEY, JSON.stringify([...set])); } catch (e) {} };

  // Der „schon gesehen"-Ring an der Bubble braucht den Viewer nicht. Seit der
  // Viewer lazy gemountet wird (siehe Bootstrap unten), muss das eigenständig
  // beim Laden laufen — sonst fehlt die Markierung bis zum ersten Öffnen.
  const markSeenBubbles = () => {
    const seen = readSeen();
    document.querySelectorAll('a[data-story="1"]').forEach((b) => {
      if (seen.has(b.dataset.storyIndex)) b.classList.add('tp-bub--seen');
    });
  };

  class TpStoryViewer extends HTMLElement {
    connectedCallback() {
      // Aus fremden Stacking-Contexts befreien: liegt das Overlay in einem Ahnen
      // mit backdrop-filter/transform/filter (z.B. die Header-Bar auf der PDP),
      // ist position:fixed dort gefangen und Header/Inhalt überlagern es trotz
      // hohem z-index. Lösung: ans <body> hängen (reconnect läuft dann normal).
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }
      this.cards = [...this.querySelectorAll('[data-story-card]')];
      this.total = this.cards.length;
      this.duration = (parseFloat(this.dataset.duration) || 7) * 1000;
      this.current = -1;
      this.paused = false;
      this._raf = null; this._start = 0; this._elapsed = 0; this._fill = null;

      this.querySelectorAll('[data-story-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.querySelector('[data-story-prev]')?.addEventListener('click', (e) => { e.stopPropagation(); this.prev(); });
      this.querySelector('[data-story-next]')?.addEventListener('click', (e) => { e.stopPropagation(); this.next(); });
      this.querySelectorAll('[data-story-pause]').forEach((el) => el.addEventListener('click', (e) => { e.stopPropagation(); this.togglePause(); }));

      this._onDocClick = (e) => {
        const b = e.target.closest('a[data-story="1"]');
        if (!b) return;
        e.preventDefault();
        const idx = parseInt(b.dataset.storyIndex, 10) || 0;
        // Spec 001: Bubble geöffnet (Spec 195 §7).
        this._track('bubble_opened', { bubble_index: idx, collection: this._collection(idx) });
        this.open(idx);
      };
      document.addEventListener('click', this._onDocClick);

      // Spec 001: "Alle ansehen"-Link im Story-Shelf → Collection-Klick. Navigation
      // läuft normal weiter; sendBeacon in tp-track überlebt den Seitenwechsel.
      this.addEventListener('click', (e) => {
        const a = e.target.closest('.tp-story__shelf-all');
        if (a) this._track('collection_clicked', { collection: this._collection(this.current) });
      });

      this._onKey = (e) => {
        if (this.hidden) return;
        if (e.key === 'Escape') this.close();
        else if (e.key === 'ArrowLeft') this.prev();
        else if (e.key === 'ArrowRight') this.next();
        else if (e.key === ' ') { e.preventDefault(); this.togglePause(); }
      };
      document.addEventListener('keydown', this._onKey);

      this._initGestures();
      this._markSeenBubbles();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKey);
      this._stopTimer();
    }

    _markSeenBubbles() {
      markSeenBubbles();
    }

    // Tracking defensiv: der Story-Viewer darf nie am Tracking scheitern.
    _track(type, props) {
      try { window.TP && window.TP.track(type, props); } catch (e) {}
    }

    _collection(i) {
      return (this.cards && this.cards[i] && this.cards[i].dataset.storyCollection) || undefined;
    }

    open(i) {
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      this._track('story_viewed', { bubble_index: i, collection: this._collection(i) });
      this.show(i);
    }

    close() {
      this._stopTimer();
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      this.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      this.current = -1;
    }

    show(i) {
      if (i < 0) i = 0;
      if (i >= this.total) { this.close(); return; }
      this.current = i;
      this.cards.forEach((c, idx) => { c.hidden = idx !== i; });
      const card = this.cards[i];
      this._loadImg(card);
      this._loadImg(this.cards[i + 1]);
      this._buildProgress(card);
      const seen = readSeen(); seen.add(String(i)); writeSeen(seen);
      document.querySelector(`a[data-story="1"][data-story-index="${i}"]`)?.classList.add('tp-bub--seen');
      this._track('story_card_viewed', { card_index: i, collection: this._collection(i) });
      this._restartTimer();
    }

    next() { this.show(this.current + 1); }
    prev() { if (this.current > 0) this.show(this.current - 1); else this._restartTimer(); }

    _loadImg(card) {
      if (!card) return;
      const img = card.querySelector('.tp-story__img');
      if (img && !img.src && img.dataset.src) {
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        // error zählt mit: sonst stünde die Story bei einem kaputten Bild für immer.
        const done = () => card.classList.add('is-loaded');
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
        img.src = img.dataset.src;
      }
    }

    _buildProgress(card) {
      const wrap = card.querySelector('[data-story-progress]');
      if (!wrap) { this._fill = null; return; }
      wrap.innerHTML = '';
      for (let k = 0; k < this.total; k++) {
        const seg = document.createElement('span');
        seg.className = 'tp-story__seg';
        const fill = document.createElement('i');
        if (k < this.current) fill.style.width = '100%';
        seg.appendChild(fill);
        wrap.appendChild(seg);
      }
      this._fill = wrap.querySelectorAll('.tp-story__seg i')[this.current] || null;
    }

    _restartTimer() {
      this._stopTimer();
      this._elapsed = 0;
      this._start = performance.now();
      this.paused = false;
      this._reflectPause();
      this._tick();
    }

    _tick() {
      this._raf = requestAnimationFrame((t) => {
        // Die Story-Zeit läuft erst, wenn das Bild da ist — vorher sah man nur den
        // Spinner, während der Balken schon 10–20 % der Story verbraucht hatte.
        const loading = !this.cards[this.current]?.classList.contains('is-loaded');
        if (this.paused || loading) { this._start = t - this._elapsed; this._tick(); return; }
        this._elapsed = t - this._start;
        const p = Math.min(1, this._elapsed / this.duration);
        if (this._fill) this._fill.style.width = (p * 100) + '%';
        if (p >= 1) { this.next(); return; }
        this._tick();
      });
    }

    _stopTimer() { if (this._raf) cancelAnimationFrame(this._raf); this._raf = null; }

    togglePause() { this.paused = !this.paused; this._reflectPause(); }

    _reflectPause() {
      this.querySelectorAll('.tp-story__ic-pause').forEach((e) => { e.hidden = this.paused; });
      this.querySelectorAll('.tp-story__ic-play').forEach((e) => { e.hidden = !this.paused; });
    }

    _initGestures() {
      const stage = this.querySelector('[data-story-stage]');
      if (!stage) return;
      const skip = (t) => t.closest('.tp-story__shelf, a, button');
      let sx = 0, sy = 0, st = 0, held = false, holdT = null;
      stage.addEventListener('pointerdown', (e) => {
        if (skip(e.target)) return;
        sx = e.clientX; sy = e.clientY; st = performance.now(); held = false;
        holdT = setTimeout(() => { held = true; this.paused = true; this._reflectPause(); }, 250);
      });
      const end = (e) => {
        clearTimeout(holdT);
        if (skip(e.target)) return;
        const dx = e.clientX - sx, dy = e.clientY - sy, dt = performance.now() - st;
        if (held) { this.paused = false; this._reflectPause(); if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return; }
        if (dy > 80 && Math.abs(dy) > Math.abs(dx)) { this.close(); return; }
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) { dx < 0 ? this.next() : this.prev(); return; }
        if (!held && dt < 250 && Math.abs(dx) < 10 && Math.abs(dy) < 10) {
          const rect = stage.getBoundingClientRect();
          (e.clientX - rect.left) < rect.width * 0.35 ? this.prev() : this.next();
        }
      };
      stage.addEventListener('pointerup', end);
      stage.addEventListener('pointercancel', () => { clearTimeout(holdT); if (held) { this.paused = false; this._reflectPause(); } });
    }
  }

  customElements.define('tp-story-viewer', TpStoryViewer);

  /* ── Lazy-Fetch + Mount (Perf-Loop I2 → I8) ─────────────────────────────────
     I2 hatte das Markup in ein <template> gelegt — das sparte ~1.500 DOM-Knoten,
     aber keine Bytes: ~100 KB roh / ~10 KB gzip standen weiter in jedem Dokument
     mit Bubbles. Seit I8 liegt das Markup in der editor-gesperrten Section
     sections/tp-story-viewer.liquid und wird bei Interaktions-Absicht per
     Section-Rendering-API geholt (gleiches Muster wie der Nav-Drawer aus I7):

       GET {routes.root}?section_id=tp-story-viewer

     pointerdown/touchstart auf einer Story-Bubble startet den Fetch ~100 ms vor
     dem click; der click selbst wird in der CAPTURE-Phase abgefangen (vor dem
     Default des <a>), als pendingIndex gemerkt und nach dem Mount ausgeführt —
     er geht also nie verloren. Beim Einhängen an <body> wird das Custom Element
     aufgewertet und connectedCallback übernimmt die weitere Delegation; dieser
     Bootstrap entfernt sich danach. Fetch-Fehler werden still geschluckt, der
     nächste Öffnen-Versuch lädt erneut (Fehlerbehandlung wie I7). */
  let mountPromise = null;
  let pendingIndex = null;

  const mountedViewer = () => document.querySelector('tp-story-viewer');

  const ensureViewer = () => {
    if (mountedViewer()) return Promise.resolve();
    if (mountPromise) return mountPromise;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    mountPromise = fetch(root + '?section_id=tp-story-viewer')
      .then((res) => {
        if (!res.ok) throw new Error('tp-story-viewer ' + res.status);
        return res.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const viewer = doc.querySelector('tp-story-viewer');
        if (!viewer) throw new Error('tp-story-viewer: Section ohne Viewer-Markup');
        // appendChild adoptiert den Knoten und wertet das Custom Element auf.
        if (!mountedViewer()) document.body.appendChild(viewer);
      })
      .catch((err) => {
        mountPromise = null; // nächster Versuch lädt neu
        throw err;
      });
    return mountPromise;
  };

  // Vorlade-Signal: Fetch schon beim Antippen starten, nicht erst beim click.
  const preload = (e) => {
    if (e.target.closest && e.target.closest('a[data-story="1"]')) ensureViewer().catch(() => {});
  };

  const openOnClick = (e) => {
    const bubble = e.target.closest && e.target.closest('a[data-story="1"]');
    if (!bubble) return;
    if (mountedViewer()) {
      // Viewer hängt schon im DOM → seine eigene Delegation übernimmt diesen
      // und alle weiteren Klicks; der Bootstrap zieht sich zurück.
      document.removeEventListener('click', openOnClick, true);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    pendingIndex = parseInt(bubble.dataset.storyIndex, 10) || 0;
    // Der erste Fetch dauert spürbar (gemessen 0,5–1,4 s). Ohne Rückmeldung wirkt
    // der Tap wirkungslos → Ring der Bubble dreht als Ladeanzeige (CSS).
    bubble.classList.add('tp-bub--loading');
    bubble.setAttribute('aria-busy', 'true');
    const unbusy = () => { bubble.classList.remove('tp-bub--loading'); bubble.removeAttribute('aria-busy'); };
    ensureViewer()
      .then(() => {
        const viewer = mountedViewer();
        if (!viewer || pendingIndex === null) return;
        const idx = pendingIndex;
        pendingIndex = null;
        document.removeEventListener('click', openOnClick, true);
        // Gleiche Reihenfolge wie im eingebauten Handler: erst bubble_opened, dann öffnen.
        viewer._track('bubble_opened', { bubble_index: idx, collection: viewer._collection(idx) });
        viewer.open(idx);
      })
      .catch(() => {
        pendingIndex = null;
        // Viewer nicht ladbar → wenigstens zur verlinkten Collection statt toter Tap.
        const href = bubble.getAttribute('href');
        if (href && href !== '#') location.href = href;
      })
      .finally(unbusy);
  };

  const boot = () => {
    markSeenBubbles();
    document.addEventListener('pointerdown', preload, true);
    document.addEventListener('touchstart', preload, { capture: true, passive: true });
    document.addEventListener('click', openOnClick, true);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme-Editor: Section-Re-Render baut die Bubbles neu auf — „schon gesehen"-
  // Ringe erneut markieren. Der Viewer selbst hängt am <body> und überlebt das.
  document.addEventListener('shopify:section:load', markSeenBubbles);
})();
